import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  generateLetterOptions,
  parseSentenceForPicker,
} from '../../utils/letterConfusables';
import type { LetterErrorContext } from '../../utils/phonicsClassifier';
import './LetterPicker.css';

/** Max wrong attempts before auto-revealing the letter */
const MAX_WRONG_ATTEMPTS = 3;

/** Error data exposed after sentence completion for phonics analysis */
export interface LetterPickerErrorData {
  expectedText: string;
  errors: Array<{ position: number; expected: string; attempted: string[] }>;
  revealedPositions: number[];
  totalLetters: number;
  /** All non-autofill expected letters (lowercase) — for tracking correct encounters */
  expectedLetters: string[];
}

interface LetterPickerProps {
  /** The expected sentence (what the user heard) */
  expectedText: string;
  /** Called when user finishes the entire sentence */
  onComplete: (userText: string) => void;
  /** Called with error/hint data after completion — for phonics analysis */
  onErrorData?: (data: LetterPickerErrorData) => void;
  /** Called immediately on each wrong letter tap — for phonics/spelling guidance.
   *  Context includes prevChar, the full word, and position within the word. */
  onLetterError?: (expected: string, attempted: string, context: LetterErrorContext) => void;
  /** Called when user taps skip (optional — if not provided, skip button is hidden) */
  onSkip?: () => void;
  /** Localized label for the skip button (defaults to "Skip"). */
  skipLabel?: string;
  /** Whether input is disabled (e.g. during submission) */
  disabled?: boolean;
  /** Number of letter options to show (default: 7) */
  optionCount?: number;
  /** Reset trigger — increment to reset state */
  resetKey?: number;
}

const LetterPicker: React.FC<LetterPickerProps> = ({
  expectedText,
  onComplete,
  onErrorData,
  onLetterError,
  onSkip,
  skipLabel,
  disabled = false,
  optionCount = 7,
  resetKey = 0,
}) => {
  const entries = useMemo(() => parseSentenceForPicker(expectedText), [expectedText]);
  const totalLetters = useMemo(() => entries.filter((e) => !e.autoFill).length, [entries]);

  // Track which character position we're at (index into entries[])
  const [currentIndex, setCurrentIndex] = useState(0);
  // Accumulated user input
  const [userChars, setUserChars] = useState<string[]>([]);
  // Wrong answer flash
  const [wrongFlash, setWrongFlash] = useState<string | null>(null);
  // Letters input so far (for progress)
  const [lettersCompleted, setLettersCompleted] = useState(0);
  // Current letter options
  const [options, setOptions] = useState<string[]>([]);
  // Track errors per position for analytics
  const errorsRef = useRef<Map<number, string[]>>(new Map());
  // Previous prompt's options — used to lock shared-letter positions across
  // consecutive prompts so finger memory stays valid.
  const prevOptionsRef = useRef<string[]>([]);
  // Wrong attempts for current position (drives auto-reveal)
  const [wrongCount, setWrongCount] = useState(0);
  // Set of positions that were revealed (hint or auto-reveal)
  const [revealedPositions, setRevealedPositions] = useState<Set<number>>(new Set());
  // Guard against double-fire of onComplete
  const completedRef = useRef(false);

  // Reset on new sentence or resetKey change
  useEffect(() => {
    setCurrentIndex(0);
    setUserChars([]);
    setLettersCompleted(0);
    setWrongFlash(null);
    setWrongCount(0);
    setRevealedPositions(new Set());
    errorsRef.current = new Map();
    prevOptionsRef.current = [];
    completedRef.current = false;
  }, [expectedText, resetKey]);

  // Auto-fill non-letter characters and generate options for current position
  useEffect(() => {
    if (currentIndex >= entries.length) return;

    // Auto-fill all consecutive non-letter chars
    let idx = currentIndex;
    const newChars = [...userChars];
    while (idx < entries.length && entries[idx].autoFill) {
      newChars[idx] = entries[idx].char;
      idx++;
    }

    if (idx !== currentIndex) {
      setUserChars(newChars);
      setCurrentIndex(idx);
      return; // useEffect will re-run for the new index
    }

    // Generate options for current letter — pass previous options so shared
    // letters stay at their previous indices (finger-memory preservation).
    if (idx < entries.length) {
      const nextOptions = generateLetterOptions(
        entries[idx].char,
        optionCount,
        prevOptionsRef.current,
      );
      setOptions(nextOptions);
      prevOptionsRef.current = nextOptions;
    }
  }, [currentIndex, entries, optionCount, userChars]);

  // Check if sentence is complete
  useEffect(() => {
    if (completedRef.current) return;
    if (currentIndex >= entries.length && entries.length > 0 && userChars.length === entries.length) {
      completedRef.current = true;
      const result = userChars.join('');
      onComplete(result);

      // Fire error data for phonics analysis
      if (onErrorData) {
        const errors: LetterPickerErrorData['errors'] = [];
        errorsRef.current.forEach((attempted, position) => {
          if (attempted.length > 0 && position < entries.length) {
            errors.push({ position, expected: entries[position].char.toLowerCase(), attempted });
          }
        });
        onErrorData({
          expectedText,
          errors,
          revealedPositions: Array.from(revealedPositions),
          totalLetters,
          expectedLetters: entries.filter(e => !e.autoFill).map(e => e.char.toLowerCase()),
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, entries.length, userChars, onComplete]);

  // Reveal the current letter (hint or auto-reveal after max wrong attempts)
  const revealCurrentLetter = useCallback(() => {
    if (disabled || currentIndex >= entries.length) return;

    const newChars = [...userChars];
    newChars[currentIndex] = entries[currentIndex].char;
    setUserChars(newChars);
    setRevealedPositions((prev) => new Set(prev).add(currentIndex));
    setLettersCompleted((prev) => prev + 1);
    setCurrentIndex(currentIndex + 1);
    setWrongFlash(null);
    setWrongCount(0);
  }, [disabled, currentIndex, entries, userChars]);

  const handleLetterTap = useCallback(
    (letter: string) => {
      if (disabled || currentIndex >= entries.length) return;

      const expected = entries[currentIndex].char.toLowerCase();
      const tapped = letter.toLowerCase();

      if (tapped === expected) {
        // Correct — preserve original case
        const newChars = [...userChars];
        newChars[currentIndex] = entries[currentIndex].char;
        setUserChars(newChars);
        setLettersCompleted((prev) => prev + 1);
        setCurrentIndex(currentIndex + 1);
        setWrongFlash(null);
        setWrongCount(0);
      } else {
        // Wrong — flash red on the tapped button
        setWrongFlash(letter);
        // Track error
        const errs = errorsRef.current.get(currentIndex) || [];
        errs.push(tapped);
        errorsRef.current.set(currentIndex, errs);
        // Fire error callback with word context for phonics/spelling classification
        if (onLetterError) {
          // Find previous non-autofill letter for digraph context
          let prevChar: string | undefined;
          for (let i = currentIndex - 1; i >= 0; i--) {
            if (!entries[i].autoFill) {
              prevChar = entries[i].char.toLowerCase();
              break;
            }
          }
          // Extract the full word surrounding the current position
          let wordStart = currentIndex;
          while (wordStart > 0 && !entries[wordStart - 1].autoFill) wordStart--;
          let wordEnd = currentIndex;
          while (wordEnd < entries.length - 1 && !entries[wordEnd + 1].autoFill) wordEnd++;
          const word = entries.slice(wordStart, wordEnd + 1).map(e => e.char.toLowerCase()).join('');
          const positionInWord = currentIndex - wordStart;
          onLetterError(expected, tapped, { prevChar, word, positionInWord });
        }

        const newWrongCount = wrongCount + 1;
        setWrongCount(newWrongCount);

        // Auto-reveal after MAX_WRONG_ATTEMPTS
        if (newWrongCount >= MAX_WRONG_ATTEMPTS) {
          setTimeout(() => revealCurrentLetter(), 400);
        } else {
          setTimeout(() => setWrongFlash(null), 400);
        }
      }
    },
    [disabled, currentIndex, entries, userChars, wrongCount, revealCurrentLetter, onLetterError],
  );

  // Render the sentence with filled/current/upcoming chars
  const renderSentence = () => {
    return (
      <div className="lp-sentence">
        {entries.map((entry, idx) => {
          const filled = idx < currentIndex;
          const isCurrent = idx === currentIndex;
          const isAutoFilled = entry.autoFill;

          const isRevealed = revealedPositions.has(idx);

          let className = 'lp-char';
          if (filled || isAutoFilled) className += ' lp-char--filled';
          if (filled && isRevealed) className += ' lp-char--revealed';
          if (isCurrent && !isAutoFilled) className += ' lp-char--current';
          if (!filled && !isCurrent && !isAutoFilled) className += ' lp-char--upcoming';

          // Display character or underscore placeholder
          let display = '';
          if (filled && userChars[idx]) {
            display = userChars[idx];
          } else if (isAutoFilled) {
            display = entry.char === ' ' ? '\u00A0' : entry.char;
          } else if (isCurrent) {
            display = '_';
          } else {
            display = '_';
          }

          return (
            <span key={idx} className={className}>
              {display}
            </span>
          );
        })}
      </div>
    );
  };

  const progressPct = totalLetters > 0 ? (lettersCompleted / totalLetters) * 100 : 0;

  return (
    <div className="letter-picker">
      {/* Sentence display */}
      <div className="lp-sentence-container">
        {renderSentence()}
      </div>

      {/* Progress bar */}
      <div className="lp-progress">
        <div className="lp-progress-bar">
          <div
            className="lp-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="lp-progress-text">
          {lettersCompleted}/{totalLetters}
        </span>
      </div>

      {/* Letter options */}
      {currentIndex < entries.length && !entries[currentIndex]?.autoFill && (
        <div className="lp-options">
          {options.map((letter) => {
            const isWrong = wrongFlash === letter;
            return (
              <button
                key={letter}
                className={`lp-option-btn ${isWrong ? 'lp-option-btn--wrong' : ''}`}
                onClick={() => handleLetterTap(letter)}
                disabled={disabled}
                type="button"
              >
                {letter}
              </button>
            );
          })}
        </div>
      )}

      {/* Hint + Skip buttons */}
      {!disabled && currentIndex < entries.length && !entries[currentIndex]?.autoFill && (
        <div className="lp-action-row">
          <button
            className="lp-hint-btn"
            onClick={revealCurrentLetter}
            type="button"
            aria-label="Hint"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6" />
              <path d="M10 22h4" />
              <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
            </svg>
          </button>
          {onSkip && (
            <button
              className="lp-skip-btn"
              onClick={onSkip}
              type="button"
            >
              {skipLabel ?? 'Skip'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LetterPicker;
